import React, { Fragment } from 'react'
import RoutedLink from 'src/components/RoutedLink'
import { notificationDuration as duration } from 'src/constants'
import { takeEvery, put, call, all } from 'redux-saga/effects'
import { replace, push } from 'connected-react-router'
import { sessionService } from 'redux-react-session'
import { actions } from './BootstrapSlice'
import { actions as configActions } from 'src/features/config/ConfigSlice'
import { notificationsEnqueue } from 'src/features/notifications/NotificationsSlice'
import { logPush } from 'src/features/contactUs/ContactUsSlice'
import { REGISTRATION_ALREADY_CONFIRMED } from 'src/services/FW5ML/errorCodes'
import FW5MLError from 'src/services/FW5ML/FW5MLError'
import autoTrack from 'src/features/tracking/autoTrack'
import { options } from 'src/services/session'
import { authTokens } from './constants'
import { initializeConfigState } from './helpers/saga/BootstrapConfigSagaHelper'
import { noticeHandler } from './helpers/saga/BootstrapNoticeSagaHelper'
import { prestitialAdRequest } from './helpers/saga/BootstrapAdsSagaHelper'
import { initializeBootstrapAuth } from './helpers/saga/BootstrapAuthSagaHelper'

/**
 * Logs debug information for bootstrap processes.
 *
 * @param {string} flow - The bootstrap flow being executed.
 * @param {string} action - The action being performed.
 * @param {Object} [details={}] - Additional details to log (optional, defaults to empty object).
 *
 * @returns {void}
 */
const debugLog = (flow, action, details = {}) => {
  console.debug(`[Bootstrap: ${flow}] ${action}`, details)
}

/**
 * Initializes the bootstrap process for the application
 *
 * @param {Function} services - Service container with dependencies
 *
 * @returns {void}
 * @throws {FW5MLError} When registration is already confirmed
 * @throws {Error} For other initialization errors
 */
function* initialize(services) {
  debugLog('initialize', 'Starting bootstrap initialization')
  const queryString = services('queryString')
  const Piwik = services('Piwik')
  const CatalogsRepository = services('CatalogsRepository')
  const configs = yield call([CatalogsRepository, 'getCatalog'], 'configs') //User for ML connection

  yield call(initializeConfigState, configs)

  try {
    // Registration Token
    const registrationToken = queryString.get(authTokens.REGISTRATION_TOKEN)

    // Initialize redux persist session
    yield call(initializeSessionService, services)

    // Initialize Piwik
    yield call([Piwik, 'initialize'])
    yield call([Piwik, 'setInitialCampaignUrlIfAny'])

    // Initialize auth flow
    yield call(initializeBootstrapAuth, services)

    // Dispatch multiple initial calls
    yield all([
      call([Piwik, 'autoTrack'], autoTrack()),
      call(noticeHandler, services),
      call(prestitialAdRequest, services, configs?.ads?.prestitialAd)
    ])

    if (registrationToken) {
      yield put(
        configActions.addConfigSuccess({
          key: 'newsletter_promo_visibility',
          data: { visible: true }
        })
      )
    }

    yield put(actions.successBootstrap())
  } catch (e) {
    console.error(e)
    yield put(logPush('Bootstrap', `Error while initializing: ${e.message}`))
    if (
      e instanceof FW5MLError &&
      e.getCode() === REGISTRATION_ALREADY_CONFIRMED
    ) {
      yield put(
        notificationsEnqueue({
          message: 'Your account has already been verified, please sign in'
        })
      )
      yield put(replace('/sign-in'))
    } else {
      yield put(push('/contact-us'))
      yield put(
        notificationsEnqueue({
          message: (
            <Fragment>
              Something went wrong! <RoutedLink label="Contact us" />
            </Fragment>
          ),
          duration
        })
      )
    }
    yield call([Piwik, 'track'], 'notification', 'error', e.message)
    yield put(actions.errorBootstrap(e.message))
  }
}

/**
 * Verify that the browser support the storage driver &
 * initialize redux persist session
 *
 * @param {Function} services - Service container with dependencies
 *
 * @returns {void}
 */
export function* initializeSessionService(services) {
  const DriverService = services('Driver')
  let sessionServiceOptions = {}

  const localStorage = yield call(DriverService.localStorageSupport)
  if (localStorage) {
    sessionServiceOptions = {
      ...options,
      driver: 'LOCALSTORAGE'
    }
  } else {
    sessionServiceOptions = {
      ...options,
      driver: 'COOKIES'
    }
  }

  sessionService.initSessionService(window.store, sessionServiceOptions)
}

export default function* watchInitialize(services) {
  yield takeEvery(actions.requestBootstrap, initialize, services)
}
